import * as THREE from 'three';

/** A single planar bevel: 6 faces, 12 edge strips, 8 corner triangles (44 tris).
 * Keeps BoxGeometry's bounds, UV orientation and six material slots. No runtime
 * subdivision, extra materials or shader work; suitable for merged/instanced meshes.
 */
export function beveledBox(w, h, d, radius = 0.08) {
  const half = [w / 2, h / 2, d / 2];
  const r = Math.min(radius, Math.min(...half) * 0.95);
  if (r <= 0) return new THREE.BoxGeometry(w, h, d).toNonIndexed();
  const inner = half.map(v => v - r);
  const buckets = Array.from({ length: 6 }, () => []);
  const point = (signs, axis) => signs.map((s, i) => s * (i === axis ? half[i] : inner[i]));
  const a = new THREE.Vector3(), b = new THREE.Vector3(), normal = new THREE.Vector3();
  function face(vertices, axis, sign) {
    a.fromArray(vertices[1]).sub(b.fromArray(vertices[0]));
    b.fromArray(vertices[2]).sub(new THREE.Vector3().fromArray(vertices[0]));
    normal.crossVectors(a, b).normalize();
    if (normal.getComponent(axis) * sign < 0) {
      vertices.reverse();
      normal.negate();
    }
    const n = normal.toArray();
    const bucket = buckets[axis * 2 + (sign < 0 ? 1 : 0)];
    for (let i = 1; i < vertices.length - 1; i++) {
      for (const p of [vertices[0], vertices[i], vertices[i + 1]]) {
        const uv = axis === 0 ? [0.5 - sign * p[2] / d, 0.5 + p[1] / h]
          : axis === 1 ? [0.5 + p[0] / w, 0.5 - sign * p[2] / d]
            : [0.5 + sign * p[0] / w, 0.5 + p[1] / h];
        bucket.push({ p, n, uv });
      }
    }
  }
  for (let axis = 0; axis < 3; axis++) {
    const u = (axis + 1) % 3, v = (axis + 2) % 3;
    for (const sign of [-1, 1]) {
      face([[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([su, sv]) => {
        const signs = []; signs[axis] = sign; signs[u] = su; signs[v] = sv;
        return point(signs, axis);
      }), axis, sign);
    }
  }
  for (let axis = 0; axis < 3; axis++) {
    for (let other = axis + 1; other < 3; other++) {
      const along = 3 - axis - other;
      for (const sign of [-1, 1]) for (const side of [-1, 1]) {
        const signs = []; signs[axis] = sign; signs[other] = side; signs[along] = -1;
        const end = [...signs]; end[along] = 1;
        face([point(signs, axis), point(signs, other), point(end, other), point(end, axis)], axis, sign);
      }
    }
  }
  for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) {
    face([0, 1, 2].map(axis => point([x, y, z], axis)), 0, x);
  }
  const positions = [], normals = [], uvs = [];
  const geo = new THREE.BufferGeometry();
  for (let i = 0; i < buckets.length; i++) {
    geo.addGroup(positions.length / 3, buckets[i].length, i);
    for (const { p, n, uv } of buckets[i]) { positions.push(...p); normals.push(...n); uvs.push(...uv); }
  }
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return geo;
}
